import React, { useEffect, useState } from 'react'
import { getNewsletterSubscribers, sendNewsletter } from '../../services/adminService'
import { toast } from 'sonner'

const formatDate = (date) => {
  if (!date) return '—'
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(date))
}

const inputStyle = {
  width: '100%',
  border: '0.5px solid #D3D1C7',
  borderRadius: 10,
  padding: '9px 12px',
  fontSize: 14,
  color: '#2A2A2A',
  background: '#FFFCF8',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
}

const Field = ({ label, hint, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#6B6B6B', letterSpacing: '0.04em' }}>
        {label}
      </label>
      {hint && <span style={{ fontSize: 11, color: '#B4B2A9' }}>{hint}</span>}
    </div>
    {children}
  </div>
)

const AdminNewsletterPage = () => {
  const [subscribers, setSubscribers] = useState([])
  const [loading, setLoading] = useState(true)

  const [subject, setSubject] = useState('')
  const [previewText, setPreviewText] = useState('')
  const [htmlContent, setHtmlContent] = useState('')
  const [sending, setSending] = useState(false)
  const [previewMode, setPreviewMode] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getNewsletterSubscribers()
        setSubscribers(data)
      } catch (err) {
        toast.error('Impossible de charger les abonnés')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const activeCount = subscribers.filter((s) => s.subscribed).length

  const handleSend = async (e) => {
    e.preventDefault()

    if (!subject.trim() || !htmlContent.trim()) {
      toast.error('Sujet et contenu sont requis')
      return
    }

    if (!confirm(`Envoyer cette newsletter à ${activeCount} abonné(s) actif(s) ?`)) return

    setSending(true)
    try {
      const result = await sendNewsletter({
        subject: subject.trim(),
        html: htmlContent.trim(),
        preview_text: previewText.trim() || undefined,
      })

      if (result?.success) {
        toast.success(`Newsletter envoyée à ${result.sent} abonné(s)`, {
          description: result.failed > 0 ? `${result.failed} échec(s)` : undefined,
        })
        setSubject('')
        setPreviewText('')
        setHtmlContent('')
        setPreviewMode(false)
      } else {
        toast.error(result?.message || "Échec de l'envoi")
      }
    } catch (err) {
      toast.error("Erreur lors de l'envoi")
      console.error(err)
    } finally {
      setSending(false)
    }
  }

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '128px 24px 48px' }}>

      {/* Header */}
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#2A2A2A' }}>
          Newsletter
        </h1>
        <p style={{ margin: '6px 0 0', fontSize: 15, color: '#8A857D' }}>
          Gérez vos abonnés et envoyez des campagnes email
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 380px', gap: 24, alignItems: 'start' }}>

        {/* ── Left : composer ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Stats bar */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 12,
          }}>
            {[
              { label: 'Total abonnés', value: subscribers.length },
              { label: 'Actifs', value: activeCount },
              { label: 'Désabonnés', value: subscribers.length - activeCount },
            ].map((s) => (
              <div key={s.label} style={{
                background: '#F5F3EE',
                borderRadius: 12,
                padding: '14px 18px',
              }}>
                <p style={{ margin: 0, fontSize: 13, color: '#8A857D' }}>{s.label}</p>
                <p style={{ margin: '4px 0 0', fontSize: 24, fontWeight: 700, color: '#2A2A2A' }}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Compose form */}
          <div style={{
            background: '#FFFCF8',
            border: '0.5px solid #E6E1D6',
            borderRadius: 16,
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: '0.5px solid #E6E1D6',
              background: '#F5F3EE',
            }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#2A2A2A' }}>
                Composer une newsletter
              </h2>
              <button
                type="button"
                onClick={() => setPreviewMode((v) => !v)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  border: '0.5px solid #D3D1C7',
                  background: previewMode ? '#2A2A2A' : 'transparent',
                  color: previewMode ? '#FFFCF8' : '#6B6B6B',
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {previewMode ? '✏️ Éditer' : '👁 Prévisualiser'}
              </button>
            </div>

            {previewMode ? (
              <div style={{ padding: 24 }}>
                <div style={{
                  border: '0.5px solid #E6E1D6',
                  borderRadius: 12,
                  overflow: 'hidden',
                }}>
                  {/* Email preview header */}
                  <div style={{
                    background: '#F5F3EE',
                    padding: '12px 16px',
                    borderBottom: '0.5px solid #E6E1D6',
                    fontSize: 13,
                    color: '#6B6B6B',
                  }}>
                    <strong>Sujet :</strong> {subject || <em>— non défini —</em>}
                    {previewText && <><br /><strong>Preview :</strong> {previewText}</>}
                  </div>
                  {/* Email body preview */}
                  <div
                    style={{ padding: 24, background: '#faf7f2', minHeight: 200 }}
                    dangerouslySetInnerHTML={{ __html: htmlContent || '<p style="color:#B4B2A9">Aucun contenu</p>' }}
                  />
                </div>
              </div>
            ) : (
              <form onSubmit={handleSend} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Field label="SUJET" hint="Objet de l'email">
                  <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="ex: ☕ Nouveautés de novembre chez Légers Coffee"
                    style={inputStyle}
                    required
                  />
                </Field>

                <Field label="TEXTE DE PRÉVISUALISATION" hint="Optionnel — aperçu dans la boîte mail">
                  <input
                    value={previewText}
                    onChange={(e) => setPreviewText(e.target.value)}
                    placeholder="ex: Découvrez nos nouvelles créations de saison..."
                    style={inputStyle}
                  />
                </Field>

                <Field
                  label="CONTENU HTML"
                  hint="HTML injecté dans le template email"
                >
                  <textarea
                    value={htmlContent}
                    onChange={(e) => setHtmlContent(e.target.value)}
                    placeholder={`<h2>Bonjour,</h2>\n<p>Voici nos nouveautés du mois...</p>\n<p>À bientôt ☕</p>`}
                    rows={12}
                    style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6, fontFamily: 'monospace', fontSize: 13 }}
                    required
                  />
                </Field>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: 8,
                  borderTop: '0.5px solid #E6E1D6',
                }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#8A857D' }}>
                    Envoi à <strong style={{ color: '#2A2A2A' }}>{activeCount}</strong> abonné(s) actif(s)
                  </p>
                  <button
                    type="submit"
                    disabled={sending || activeCount === 0}
                    style={{
                      padding: '10px 24px',
                      borderRadius: 10,
                      border: 'none',
                      background: sending || activeCount === 0 ? '#D3D1C7' : '#2A2A2A',
                      color: '#FFFCF8',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: sending || activeCount === 0 ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {sending ? 'Envoi en cours...' : '📨 Envoyer la newsletter'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* ── Right : subscribers list ── */}
        <div style={{
          background: '#FFFCF8',
          border: '0.5px solid #E6E1D6',
          borderRadius: 16,
          overflow: 'hidden',
          position: 'sticky',
          top: 100,
        }}>
          <div style={{
            padding: '14px 18px',
            borderBottom: '0.5px solid #E6E1D6',
            background: '#F5F3EE',
          }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#2A2A2A' }}>
              Abonnés
            </h2>
          </div>

          {loading ? (
            <p style={{ padding: '24px 18px', color: '#8A857D', fontSize: 14 }}>
              Chargement...
            </p>
          ) : subscribers.length === 0 ? (
            <p style={{ padding: '24px 18px', color: '#8A857D', fontSize: 14 }}>
              Aucun abonné pour le moment.
            </p>
          ) : (
            <div style={{ maxHeight: 520, overflowY: 'auto' }}>
              {subscribers.map((s, index) => (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '11px 18px',
                    borderBottom: index < subscribers.length - 1 ? '0.5px solid #F1ECE4' : 'none',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p style={{
                      margin: 0,
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#2A2A2A',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {s.email}
                    </p>
                    <p style={{ margin: '1px 0 0', fontSize: 11, color: '#B4B2A9' }}>
                      {s.first_name || '—'} · {formatDate(s.created_at)}
                    </p>
                  </div>
                  <span style={{
                    flexShrink: 0,
                    marginLeft: 12,
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 500,
                    background: s.subscribed ? '#EAF3DE' : '#FCEBEB',
                    color: s.subscribed ? '#3B6D11' : '#A32D2D',
                  }}>
                    {s.subscribed ? 'Actif' : 'Désabonné'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

export default AdminNewsletterPage